import React, { lazy } from 'react';

// Retry wrapper for lazy imports — handles stale chunks after Vercel redeploys
function lazyRetry(importFn: () => Promise<{ default: React.ComponentType<any> }>) {
  return lazy(() =>
    importFn().catch((error) => {
      // On chunk load failure, try a cache-busting reload once
      const reloadedKey = 'chunk_retry_reloaded';
      if (!sessionStorage.getItem(reloadedKey)) {
        sessionStorage.setItem(reloadedKey, '1');
        window.location.reload();
      } else {
        sessionStorage.removeItem(reloadedKey);
      }
      throw error;
    })
  );
}

// Lazy load all page components for code splitting
// All pages are now organized under /pages directory

// Public pages (unprotected)
const HomePage = lazyRetry(() => import('../pages/home/HomePage'));

// Auth pages (unprotected)
const LoginPage = lazyRetry(() => import('../pages/auth/LoginPage'));
const SignupPage = lazyRetry(() => import('../pages/auth/SignupPage'));
const ForgotPasswordPage = lazyRetry(() => import('../pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazyRetry(() => import('../pages/auth/ResetPasswordPage'));

// Dashboard
const DashboardPage = lazyRetry(() => import('../pages/dashboard/DashboardPage'));

// Issuers
const IssuerListPage = lazyRetry(() => import('../pages/issuers/IssuerListPage'));
const IssuerDetailPage = lazyRetry(() => import('../pages/issuers/IssuerDetailPage'));

// Securities
const SecuritiesListPage = lazyRetry(() => import('../pages/securities/SecuritiesListPage'));

// Claims
const ClaimsListPage = lazyRetry(() => import('../pages/claims/ClaimsListPage'));
const AddClaimPage = lazyRetry(() => import('../pages/claims/AddClaimPage'));
const ClaimDetailPage = lazyRetry(() => import('../pages/claims/ClaimDetailPage'));

// Evidence
const EvidenceListPage = lazyRetry(() => import('../pages/evidence/EvidenceListPage'));
const EvidenceDetailPage = lazyRetry(() => import('../pages/evidence/EvidenceDetailPage'));

// Screens
const ScreensListPage = lazyRetry(() => import('../pages/screens/ScreensListPage'));
const ScreenDetailPage = lazyRetry(() => import('../pages/screens/ScreenDetailPage'));
const ScreenEditPage = lazyRetry(() => import('../pages/screens/ScreenEditPage'));
const ScreenBuilderPage = lazyRetry(() => import('../pages/screens/ScreenBuilderPage'));

// Characteristics
const CharacteristicsListPage = lazyRetry(() => import('../pages/characteristics/CharacteristicsListPage'));
const CharacteristicDetailPage = lazyRetry(() => import('../pages/characteristics/CharacteristicDetailPage'));

// Teams
const TeamListPage = lazyRetry(() => import('../pages/teams/TeamListPage'));


// Portfolios
const PortfoliosListPage = lazyRetry(() => import('../pages/portfolios/PortfoliosListPage'));
const PortfolioComparisonPage = lazyRetry(() => import('../pages/portfolios/PortfolioComparisonPage'));
const PortfolioDetailPage = lazyRetry(() => import('../pages/portfolios/PortfolioDetailPage'));
const EvaluationDetailPage = lazyRetry(() => import('../pages/portfolios/EvaluationDetailPage'));
const EvaluationComparisonPage = lazyRetry(() => import('../pages/portfolios/EvaluationComparisonPage'));

// Proxy Voting
const ProposalBrowserPage = lazyRetry(() => import('../pages/proxy-voting/ProposalBrowserPage'));
const FundVoteComparisonPage = lazyRetry(() => import('../pages/proxy-voting/FundVoteComparisonPage'));
const Def14aFilingsPage = lazyRetry(() => import('../pages/proxy-voting/Def14aFilingsPage'));
const ProxyStatementDetailPage = lazyRetry(() => import('../pages/proxy-voting/ProxyStatementDetailPage'));
const ProposalDetailPage = lazyRetry(() => import('../pages/proxy-voting/ProposalDetailPage'));

// Profile
const ProfileSettingsPage = lazyRetry(() => import('../pages/profile/ProfileSettingsPage'));

// Legal
const TermsPage = lazyRetry(() => import('../pages/legal/TermsPage'));

// Contact
const ContactSalesPage = lazyRetry(() => import('../pages/contact/ContactSalesPage'));

// Invitations
const AcceptInvitationPage = lazyRetry(() => import('../pages/invitations/AcceptInvitationPage'));

// Demo
const DemoPage = lazyRetry(() => import('../pages/demo/DemoPage'));

// OAuth
const OAuthConsentPage = lazyRetry(() => import('../pages/OAuthConsentPage'));

// Review Dashboard
const ReviewDashboardPage = lazyRetry(() => import('../pages/review-dashboard/ReviewDashboardPage'));
const TaskListPage = lazyRetry(() => import('../pages/review-dashboard/TaskListPage'));
const IssuerPriorityPage = lazyRetry(() => import('../pages/review-dashboard/IssuerPriorityPage'));
const CostDashboardPage = lazyRetry(() => import('../pages/review-dashboard/CostDashboardPage'));
const JobsListPage = lazyRetry(() => import('../pages/review-dashboard/JobsListPage'));
const JobDetailPage = lazyRetry(() => import('../pages/review-dashboard/JobDetailPage'));
const TaskDetailPage = lazyRetry(() => import('../pages/review-dashboard/TaskDetailPage'));
const PromptDetailPage = lazyRetry(() => import('../pages/review-dashboard/PromptDetailPage'));

// Admin
const AdminUsersPage = lazyRetry(() => import('../pages/admin/AdminUsersPage'));
const AdminTeamsPage = lazyRetry(() => import('../pages/admin/AdminTeamsPage'));
const AdminTeamDetailPage = lazyRetry(() => import('../pages/admin/AdminTeamDetailPage'));
const AdminRoleEntitlementsPage = lazyRetry(() => import('../pages/admin/AdminRoleEntitlementsPage'));
const AdminPlanEntitlementsPage = lazyRetry(() => import('../pages/admin/AdminPlanEntitlementsPage'));
const AdminAgentCoveragePage = lazyRetry(() => import('../pages/admin/AdminAgentCoveragePage'));
const UtilitiesPage = lazyRetry(() => import('../pages/admin/UtilitiesPage'));

export interface RouteConfig {
  path: string;
  element: React.ReactNode;
  isProtected?: boolean;
}

// Unprotected routes (public pages and auth pages)
export const publicRoutes: RouteConfig[] = [
  { path: '/', element: <HomePage />, isProtected: false },
  { path: '/login', element: <LoginPage />, isProtected: false },
  { path: '/signup', element: <SignupPage />, isProtected: false },
  { path: '/forgot-password', element: <ForgotPasswordPage />, isProtected: false },
  { path: '/reset-password', element: <ResetPasswordPage />, isProtected: false },
  { path: '/contact-us', element: <ContactSalesPage />, isProtected: false },
  { path: '/demo', element: <DemoPage />, isProtected: false },
  { path: '/invitations/:token', element: <AcceptInvitationPage />, isProtected: false },
  { path: '/oauth/consent', element: <OAuthConsentPage />, isProtected: false },
];

// Protected routes (require authentication)
export const protectedRoutes: RouteConfig[] = [
  // Dashboard
  { path: '/dashboard', element: <DashboardPage /> },

  // Issuers
  { path: '/issuers', element: <IssuerListPage /> },
  { path: '/issuers/:issuerId', element: <IssuerDetailPage /> },

  // Securities
  { path: '/securities', element: <SecuritiesListPage /> },

  // Claims
  { path: '/claims', element: <ClaimsListPage /> },
  { path: '/claims/new', element: <AddClaimPage /> },
  { path: '/claims/:claimId', element: <ClaimDetailPage /> },

  // Evidence
  { path: '/evidence', element: <EvidenceListPage /> },
  { path: '/evidence/:evidenceId', element: <EvidenceDetailPage /> },

  // Screens
  { path: '/screens', element: <ScreensListPage /> },
  { path: '/screens/new', element: <ScreenBuilderPage /> },
  { path: '/screens/:screenId', element: <ScreenDetailPage /> },
  { path: '/screens/:screenId/edit', element: <ScreenBuilderPage /> },
  // Per-characteristic evidence policy overrides, which the builder doesn't edit
  { path: '/screens/:screenId/edit/advanced', element: <ScreenEditPage /> },

  // Characteristics
  { path: '/characteristics', element: <CharacteristicsListPage /> },
  { path: '/characteristics/:characteristicId', element: <CharacteristicDetailPage /> },

  // Teams
  { path: '/teams', element: <TeamListPage /> },

  // Profile
  { path: '/profile', element: <ProfileSettingsPage /> },

  // Legal — protected so the terms stay unavailable to unauthenticated
  // visitors. Intentionally carries no `view_page_*` entitlement: see the note
  // in TermsPage.tsx.
  { path: '/terms', element: <TermsPage /> },

  // Portfolios
  { path: '/portfolios', element: <PortfoliosListPage /> },
  { path: '/portfolios/compare', element: <PortfolioComparisonPage /> },
  { path: '/portfolios/:portfolioId', element: <PortfolioDetailPage /> },
  { path: '/portfolios/:portfolioId/evaluations/:evaluationId', element: <EvaluationDetailPage /> },
  { path: '/portfolios/:portfolioId/evaluations/:evaluationId/compare/:comparisonId', element: <EvaluationComparisonPage /> },

  // Proxy Voting
  { path: '/proxy-proposals', element: <ProposalBrowserPage /> },
  { path: '/proxy-proposals/compare', element: <FundVoteComparisonPage /> },
  { path: '/proxy-proposals/def14a', element: <Def14aFilingsPage /> },
  { path: '/proxy-proposals/def14a/:filingId', element: <ProxyStatementDetailPage /> },
  { path: '/proxy-proposals/:proposalId', element: <ProposalDetailPage /> },

  // Review Dashboard
  { path: '/reviews-dashboard', element: <ReviewDashboardPage /> },
  { path: '/reviews-dashboard/tasks/:taskId', element: <TaskDetailPage /> },
  { path: '/reviews-dashboard/tasks', element: <TaskListPage /> },
  { path: '/reviews-dashboard/priorities', element: <IssuerPriorityPage /> },
  { path: '/reviews-dashboard/costs', element: <CostDashboardPage /> },
  { path: '/reviews-dashboard/prompts/:promptId', element: <PromptDetailPage /> },
  { path: '/reviews-dashboard/jobs', element: <JobsListPage /> },
  { path: '/reviews-dashboard/jobs/:jobId', element: <JobDetailPage /> },

  // Admin
  { path: '/utilities', element: <UtilitiesPage /> },
  { path: '/admin/users', element: <AdminUsersPage /> },
  { path: '/admin/teams', element: <AdminTeamsPage /> },
  { path: '/admin/teams/:teamId', element: <AdminTeamDetailPage /> },
  { path: '/admin/role-entitlements', element: <AdminRoleEntitlementsPage /> },
  { path: '/admin/plan-entitlements', element: <AdminPlanEntitlementsPage /> },
  { path: '/admin/agent-coverage', element: <AdminAgentCoveragePage /> },
];
